import React from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Link,
} from "@mui/material";
import {
  MoreVertical,
  Package,
  CreditCard,
  ShoppingCart,
  Users,
  FileText,
  AlertCircle,
  CheckCircle2,
  Trash2,
  CheckCircle,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  format,
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
  formatDistanceToNow,
} from "date-fns";
import { Link as RouterLink } from "react-router-dom";

const NotificationCard = ({ notification, onDelete, onMarkAsRead }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const { id, type, title, message, timestamp, isRead, link } = notification;

  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);

    if (isToday(date)) {
      return `Today at ${format(date, "h:mm a")}`;
    } else if (isYesterday(date)) {
      return `Yesterday at ${format(date, "h:mm a")}`;
    } else if (isThisWeek(date)) {
      return format(date, "EEEE 'at' h:mm a");
    } else if (isThisMonth(date)) {
      return format(date, "MMM d 'at' h:mm a");
    } else {
      return format(date, "MMM d, yyyy 'at' h:mm a");
    }
  };

  const handleClick = () => {
    if (link && !isRead) {
      onMarkAsRead();
    }
    if (link) {
      navigate(link);
    }
  };

  const handleMenuOpen = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = event => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleMarkAsRead = event => {
    event.stopPropagation();
    onMarkAsRead();
    handleMenuClose();
  };

  const handleDelete = event => {
    event.stopPropagation();
    onDelete();
    handleMenuClose();
  };

  const getNotificationIcon = () => {
    switch (type) {
      case "order":
        return <Package size={20} color="#5652B0" />;
      case "payment":
        return <CreditCard size={20} color="#10B981" />;
      case "inventory":
        return <ShoppingCart size={20} color="#F59E0B" />;
      case "customer":
        return <Users size={20} color="#3B82F6" />;
      case "request":
        return <FileText size={20} color="#8B5CF6" />;
      default:
        return <AlertCircle size={20} color="#EF4444" />;
    }
  };

  const getNotificationTypeLabel = () => {
    switch (type) {
      case "order":
        return "Order";
      case "payment":
        return "Payment";
      case "inventory":
        return "Inventory";
      case "customer":
        return "Customer";
      case "request":
        return "Request";
      default:
        return "Alert";
    }
  };

  const getNotificationTypeColor = () => {
    switch (type) {
      case "order":
        return "#5652B0";
      case "payment":
        return "#10B981";
      case "inventory":
        return "#F59E0B";
      case "customer":
        return "#3B82F6";
      case "request":
        return "#8B5CF6";
      default:
        return "#EF4444";
    }
  };

  const formattedTime = formatDistanceToNow(new Date(timestamp), {
    addSuffix: true,
  });

  const typeColor = {
    bg:
      getNotificationTypeColor() === "#5652B0"
        ? "#EEF2FF"
        : getNotificationTypeColor() === "#10B981"
        ? "#ECFDF5"
        : getNotificationTypeColor() === "#F59E0B"
        ? "#FEF2F2"
        : getNotificationTypeColor() === "#3B82F6"
        ? "#F0F9FF"
        : "#FEF9C3",
    text: getNotificationTypeColor(),
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: isRead ? "#E5E7EB" : "#D1D5DB",
        backgroundColor: isRead ? "#FFFFFF" : "#F9FAFB",
        position: "relative",
        transition: "all 0.2s ease",
        cursor: link ? "pointer" : "default",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
        },
      }}
      onClick={handleClick}>
      {!isRead && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: "4px",
            height: "50%",
            backgroundColor: "#5652B0",
            borderRadius: "0 4px 4px 0",
          }}
        />
      )}

      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: typeColor.bg,
            color: typeColor.text,
            mr: 2,
          }}>
          {getNotificationIcon()}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: isRead ? 500 : 600,
                color: "#1F2937",
              }}>
              {title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#6B7280",
                ml: 1,
              }}>
              {formattedTime}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "#4B5563",
              mb: 1.5,
              lineHeight: 1.5,
            }}>
            {message}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Chip
                label={getNotificationTypeLabel()}
                size="small"
                sx={{
                  backgroundColor: typeColor.bg,
                  color: typeColor.text,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  height: 24,
                }}
              />

              {link && (
                <Link
                  component={RouterLink}
                  to={link}
                  underline="hover"
                  sx={{
                    ml: 2,
                    display: "flex",
                    alignItems: "center",
                    color: "#5652B0",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    "&:hover": { color: "#433EA0" },
                  }}>
                  View details
                  <ExternalLink size={14} style={{ marginLeft: 4 }} />
                </Link>
              )}
            </Box>

            <Box>
              {!isRead && (
                <Tooltip title="Mark as read">
                  <IconButton
                    onClick={handleMarkAsRead}
                    size="small"
                    sx={{
                      color: "#5652B0",
                      "&:hover": { backgroundColor: "rgba(86, 82, 176, 0.08)" },
                    }}>
                    <CheckCircle size={18} />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Delete notification">
                <IconButton
                  onClick={handleDelete}
                  size="small"
                  sx={{
                    color: "#EF4444",
                    "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" },
                  }}>
                  <Trash2 size={18} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default NotificationCard;
